import React, { useState, useEffect, useRef } from "react";
import { num, isAmount } from "../../../utils/num";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { listTargets, sendCoins, getTransactions, reset } from "../../../store/actions/performanceSlice";
import { updateUserCoins } from "../../../store/actions/AuthSlice";
import UniToast from "../../../Components/UniToast/UniToast";
import Pagination from "../../../Components/Pagination/Pagination";
import { transformToPaginationMeta } from "../../../utils/pagination";
import greenIcon from "../../../Assets/Balance/Icon-green.png";
import redIcon from "../../../Assets/Balance/icon-red.png";
import coinIcon from "../../../Assets/Balance/coin.png";
import AOS from 'aos';
import 'aos/dist/aos.css';

function SendCoins() {
  const dispatch = useDispatch();
  const { categoryDetails, loading,SendCoinsloading , error, coinsAdded, transactionsData } = useSelector((state) => state.performance);
  const { data} = useSelector((state) => state.auth);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [coins, setCoins] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [animatingCoins, setAnimatingCoins] = useState([]);
  const [lastSentAmount, setLastSentAmount] = useState(0);
  
  const autocompleteRef = useRef(null);
  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 600,
      easing: 'ease-out-cubic',
      once: true,
      mirror: false,
      offset: 50,
    });
  }, []);

  // Fetch targets data and transactions on component mount
  useEffect(() => {
    // You may need to provide a categoryId - adjust this based on your needs
    dispatch(listTargets({ id: data?.id, page: 1, limit: 100 }));
    dispatch(getTransactions({ page: currentPage, limit: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  // Refresh transactions when coins are successfully sent
  useEffect(() => {
    if (coinsAdded) {
      setCurrentPage(1); // Reset to first page
      dispatch(getTransactions({ page: 1, limit: itemsPerPage }));
      dispatch(reset())
    }
  }, [coinsAdded, dispatch, itemsPerPage]);

  // Update user coins after successful send
  useEffect(() => {
    if (coinsAdded && lastSentAmount > 0 && data?.coins !== undefined) {
      const newCoinsBalance = data.coins - lastSentAmount;
      dispatch(updateUserCoins(newCoinsBalance));
      setLastSentAmount(0); // Reset the sent amount
    }
  }, [coinsAdded, lastSentAmount, data?.coins, dispatch]);

  // Filter users based on search term
  useEffect(() => {
    if (categoryDetails?.data) {
      if (searchTerm.length > 0) {
        const filtered = categoryDetails.data.filter(target => 
          target.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          target.user?.nick_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredUsers(filtered);
      } else {
        // Show all users when no search term
        setFilteredUsers(categoryDetails.data);
      }
    } else {
      setFilteredUsers([]);
    }
  }, [searchTerm, categoryDetails]);

  // Handle success and error states for sendCoins
  useEffect(() => {
    if (coinsAdded || error) {
      setShowToast(true);
      
      // Trigger success animation when coins are added
      if (coinsAdded) {
        triggerSuccessAnimation();
      }
    }
  }, [coinsAdded, error]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.user?.name || user.user?.nick_name || "");
    setShowSuggestions(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedUser(null);
  };

  const handleSendCoins = () => {
    // Decimals allowed (up to 4); the backend re-checks the balance.
    if (selectedUser && isAmount(coins) && parseFloat(coins) <= Number(data?.coins || 0)) {
      const coinsToSend = String(coins).trim();
      setLastSentAmount(coinsToSend); // Store the amount before resetting
      
      dispatch(sendCoins({ 
        userId: selectedUser.user?.id, 
        coins: coinsToSend
      }));
      
      // Reset form after sending
      setSearchTerm("");
      setSelectedUser(null);
      setCoins("");
    }
  };

  // Handle coin input change with validation
  const handleCoinsChange = (e) => {
    const value = e.target.value;
    const numValue = parseFloat(value);
    const maxCoins = Number(data?.coins || 0);
    
    // Allow empty string for clearing input
    if (value === "") {
      setCoins("");
      return;
    }
    
    // Prevent negative values and values greater than available coins
    if (numValue > 0 && numValue <= maxCoins) {
      setCoins(value);
    } else if (numValue > maxCoins) {
      setCoins(maxCoins.toString());
    }
  };

  // Check if user can send coins
  const canSendCoins = () => {
    const userCoins = data?.coins || 0;
    const coinsToSend = parseFloat(coins) || 0;
    return userCoins > 0 && isAmount(coins) && coinsToSend <= userCoins && selectedUser;
  };

  // Get validation message
  const getValidationMessage = () => {
    const userCoins = data?.coins || 0;
    const coinsToSend = parseFloat(coins) || 0;
    
    if (coins && !isAmount(coins)) {
      return "Up to 4 decimals";
    }
    if (userCoins === 0) {
      return "You don't have any coins to send";
    }
    if (coinsToSend > userCoins) {
      return `You can only send up to ${num(userCoins)} coins`;
    }
    return "";
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleDateString('en-US', options);
  };

  // Helper function to determine transaction type and amount
  const getTransactionDisplay = (transaction) => {
    // Assuming the transaction has amount and type fields
    // You may need to adjust this based on your actual API response structure
    const amount = transaction.amount || transaction.coins || 0;
    const isPositive = amount > 0;
    
    // transaction_category rows are always a category -> user send
    // (see sendCoins() server-side); there is no "received" direction here.
    return {
      icon: redIcon,
      color: "#F04C4D",
      amount: `-${num(amount)}`,
      type: transaction.type || "Sent"
    };
  };

  // Handle pagination page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(getTransactions({ page, limit: itemsPerPage }));
  };

  // Trigger success animation
  const triggerSuccessAnimation = () => {
    setShowSuccessAnimation(true);
    
    // Create flying coins animation
    const coinsArray = [];
    for (let i = 0; i < 8; i++) {
      coinsArray.push({
        id: i,
        delay: i * 100,
        startX: Math.random() * 100,
        startY: Math.random() * 100,
        endX: Math.random() * 100,
        endY: Math.random() * 100,
      });
    }
    setAnimatingCoins(coinsArray);
    
    // Hide animation after 3 seconds
    setTimeout(() => {
      setShowSuccessAnimation(false);
      setAnimatingCoins([]);
    }, 3000);
  };

  // Get pagination meta data
  const paginationMeta = transformToPaginationMeta(transactionsData, itemsPerPage);

  return (
    <>
      {/* Success Animation Overlay */}
      {showSuccessAnimation && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            pointerEvents: 'none',
            overflow: 'hidden'
          }}
        >
          {/* Success Message */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'linear-gradient(135deg, #01B574, #00a65a)',
              color: 'white',
              padding: '30px 50px',
              borderRadius: '20px',
              fontSize: '24px',
              fontWeight: 'bold',
              boxShadow: '0 10px 30px rgba(1, 181, 116, 0.4)',
              animation: 'successPulse 3s ease-in-out',
              textAlign: 'center',
              minWidth: '300px'
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎉</div>
            <div>Coins Sent Successfully!</div>
            <div style={{ fontSize: '16px', marginTop: '10px', opacity: 0.9 }}>
              Your transaction has been completed
            </div>
          </div>

          {/* Flying Coins */}
          {animatingCoins.map((coin) => (
            <img
              key={coin.id}
              src={coinIcon}
              alt="coin"
              style={{
                position: 'absolute',
                width: '40px',
                height: '40px',
                left: `${coin.startX}%`,
                top: `${coin.startY}%`,
                animation: `flyingCoin 2s ease-out ${coin.delay}ms forwards`,
                transform: 'rotate(0deg)',
                filter: 'drop-shadow(0 4px 8px rgba(255, 215, 0, 0.6))',
              }}
            />
          ))}

          {/* Confetti Effect */}
          <div
            style={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '100px',
              animation: 'confettiExplosion 2s ease-out'
            }}
          >
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: '8px',
                  height: '8px',
                  backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][i % 5],
                  borderRadius: '50%',
                  animation: `confettiPiece 2s ease-out ${i * 50}ms forwards`,
                  left: '50%',
                  top: '50%'
                }}
              />
            ))}
          </div>
        </div>
      )}

      <Row>
      <Col md={8} data-aos="fade-right" data-aos-delay="100">
        <div className="d-flex text-white align-items-center justify-content-center flex-column gap-4">
          <h3 className="mb-4" style={{ transform: "translate(-80px)" }} >
            Send Coins to User
          </h3>
          
          {/* User Search Autocomplete */}
          <div className="position-relative col-12 mb-4" ref={autocompleteRef}>
            <div className="d-flex align-items-stretch justify-content-center">
              <div className="position-relative col-10">
                <input
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowSuggestions(true)}
                  className="w-100 border-0 p-4 rounded-pill text-white"
                  type="text"
                  placeholder="Search user by name, username, or ID..."
                  style={{
                    backgroundColor: '#2a2a2a',
                    fontSize: '16px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.boxShadow = '0 4px 15px rgba(0,123,255,0.2)';
                    setShowSuggestions(true);
                  }}
                  onMouseLeave={(e) => e.target.style.boxShadow = '0 2px 10px rgba(0,0,0,0.3)'}
                />
                
                {/* Autocomplete Suggestions */}
                {showSuggestions && filteredUsers.length > 0 && (
                  <div 
                    className="position-absolute w-100 rounded-3 mt-2"
                    style={{ 
                      zIndex: 1000,
                      maxHeight: '400px',
                      overflowY: 'auto',
                      overflowX:'hidden',
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.4)' 
                    }}
                  >
                    {filteredUsers.slice(0, 5).map((target, index) => (
                      <div
                        key={target.id}
                        className="p-3 text-white"
                        style={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          borderBottom: index < filteredUsers.slice(0, 5).length - 1 ? '1px solid #333' : 'none',
                          minHeight: '60px' ,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#007bff';
                          e.currentTarget.style.transform = 'translateX(5px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.transform = 'translateX(0px)';
                        }}
                        onClick={() => handleUserSelect(target)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-bold mb-1" style={{ fontSize: '16px' }}>
                              {target.user?.name ? `@${target.user.name}` : 'N/A'}
                            </div>
                            <div className="" style={{ fontSize: '14px' }}>
                              {target.user?.nick_name || 'N/A'}
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="fw-bold" style={{ color: '#007bff', fontSize: '14px' }}>
                              {target.coins} coins
                            </div>
                            <div className="" style={{ fontSize: '12px' }}>
                              Available
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* No results message */}
                {showSuggestions && searchTerm && filteredUsers.length === 0 && (
                  <div 
                    className="position-absolute w-100 rounded-3 mt-2 p-4 text-center "
                    style={{ 
                      zIndex: 1000,
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.4)'
                    }}
                  >
                    No users found matching "{searchTerm}"
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Available Coins Display */}
          <div className="text-center mb-3" data-aos="fade-up" data-aos-delay="350">
            <div className="d-flex align-items-center justify-content-center gap-2">
              <img src={coinIcon} alt="coin" style={{ width: '24px', height: '24px' }} />
              <span className="text-white" style={{ fontSize: '18px', fontWeight: '500' }}>
                Available: <span style={{ color: '#FFD700', fontWeight: 'bold' }}>{num(data?.coins)}</span> coins
              </span>
            </div>
            {(data?.coins || 0) === 0 && (
              <div className="mt-2" style={{ color: '#F04C4D', fontSize: '14px' }}>
                <i className="fas fa-exclamation-triangle me-1"></i>
                You need coins to send to other users
              </div>
            )}
          </div>

          {/* Coins Input Section */}
          <div className="d-flex text-white align-items-center justify-content-center flex-column gap-3" data-aos="fade-up" data-aos-delay="400">
            <h3 className="" style={{ transform: "translate(-80px)" }}>
              Amount to Send
            </h3>
            <div className="d-flex align-items-stretch justify-content-center col-12 gap-3" >
              <div className="col-8 position-relative">
                <input
                  value={coins}
                  onChange={handleCoinsChange}
                  className="w-100 border-0 p-4 rounded-pill text-white"
                  type="number"
                  placeholder={`Enter amount (max: ${num(data?.coins)})...`}
                  min="0"
                  step="any"
                  inputMode="decimal"
                  max={data?.coins || 0}
                  disabled={(data?.coins || 0) === 0}
                  style={{
                    backgroundColor: (data?.coins || 0) === 0 ? '#1a1a1a' : '#2a2a2a',
                    fontSize: '16px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease',
                    cursor: (data?.coins || 0) === 0 ? 'not-allowed' : 'text'
                  }}
                  onMouseEnter={(e) => {
                    if ((data?.coins || 0) > 0) {
                      e.target.style.boxShadow = '0 4px 15px rgba(0,123,255,0.2)';
                    }
                  }}
                  onMouseLeave={(e) => e.target.style.boxShadow = '0 2px 10px rgba(0,0,0,0.3)'}
                />
                
                {/* Quick amount buttons */}
                
                {/* Validation message */}
                {getValidationMessage() && (
                  <div className="mt-2 text-center" style={{ color: '#F04C4D', fontSize: '14px' }}>
                    <i className="fas fa-exclamation-circle me-1"></i>
                    {getValidationMessage()}
                  </div>
                )}
              </div>
              <Button
                disabled={!canSendCoins() || SendCoinsloading}
                onClick={handleSendCoins}
                className="border-0 fw-bold text-uppercase px-5 py-3"
                style={{
                  background: !canSendCoins() || SendCoinsloading 
                    ? 'linear-gradient(135deg, #666, #444)' 
                    : 'linear-gradient(135deg, #007bff, #0056b3)',
                  borderRadius: '50px',
                  fontSize: '16px',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  cursor: !canSendCoins() || SendCoinsloading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: !canSendCoins() || SendCoinsloading 
                    ? 'none' 
                    : '0 4px 15px rgba(0,123,255,0.4)',
                  transform: 'translateY(0px)'
                }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0,123,255,0.6)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.transform = 'translateY(0px)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0,123,255,0.4)';
                  }
                }}
              >
                {SendCoinsloading ? (
                  <div className="d-flex align-items-center gap-2">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    Sending...
                  </div>
                ) : (
                  'Send Coins'
                )}
              </Button>
            </div>
          </div>
        </div>
      </Col>
      <Col
        md={4}
        style={{ backgroundColor: "#212121" }}
        className="p-4 text-white rounded"
        data-aos="fade-left"
        data-aos-delay="200"
      >
        <h5 className="text-center mb-5" data-aos="fade-down" data-aos-delay="300">Transactions</h5>
        <div className="d-flex flex-column gap-4" style={{ maxHeight: '400px', overflowY: 'auto' , overflowX:'hidden' }} data-aos="fade-up" data-aos-delay="400">
          {loading && !transactionsData?.data ? (
            <div className="text-center">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              Loading transactions...
            </div>
          ) : !transactionsData ? (
            <div className="text-center py-4">
              <div className="mb-2">
                <i className="fas fa-exclamation-circle" style={{ fontSize: '24px', color: '#6c757d' }}></i>
              </div>
              <p className="mb-0">No transaction data available</p>
              <small>Please try refreshing the page</small>
            </div>
          ) : !transactionsData.data ? (
            <div className="text-center py-4">
              <div className="mb-2">
                <i className="fas fa-database" style={{ fontSize: '24px', color: '#6c757d' }}></i>
              </div>
              <p className="mb-0">No transaction data found</p>
              <small>Transaction data is not available</small>
            </div>
          ) : transactionsData.data.length === 0 ? (
            <div className="text-center py-4">
              <div className="mb-2">
                <i className="fas fa-inbox" style={{ fontSize: '24px', color: '#6c757d' }}></i>
              </div>
              <p className="mb-0">No transactions yet</p>
              <small>Your transactions will appear here once you start sending coins</small>
            </div>
          ) : (
            transactionsData.data.map((transaction, index) => {
              const display = getTransactionDisplay(transaction);
              return (
                <div 
                  key={transaction.id || index} 
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.transform = 'translateX(0px)';
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <img src={display.icon} alt="" style={{ transition: 'transform 0.3s ease' }} />
                    <div>
                      <h6>{display.type}</h6>
                      <span className="text-grey" style={{ fontSize: '12px' }}>
                        {formatDate(transaction.created_at || transaction.createdAt || transaction.date)}
                      </span>
                    </div>
                  </div>
                  <h6 style={{ color: display.color, transition: 'all 0.3s ease' }}>{display.amount}</h6>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Component */}
        {paginationMeta && (
          <div className="mt-4" data-aos="fade-up" data-aos-delay="500">
            <Pagination
              meta={paginationMeta}
              onPageChange={handlePageChange}
              loading={loading}
              className="pagination-dark"
            />
          </div>
        )}
      </Col>
  
    </Row>

    {/* CSS Animations */}
    <style>{`
      @keyframes successPulse {
        0% {
          transform: translate(-50%, -50%) scale(0.5);
          opacity: 0;
        }
        20% {
          transform: translate(-50%, -50%) scale(1.1);
          opacity: 1;
        }
        40% {
          transform: translate(-50%, -50%) scale(1);
        }
        80% {
          transform: translate(-50%, -50%) scale(1);
          opacity: 1;
        }
        100% {
          transform: translate(-50%, -50%) scale(0.9);
          opacity: 0;
        }
      }

      @keyframes flyingCoin {
        0% {
          transform: rotate(0deg) scale(1);
          opacity: 1;
        }
        50% {
          transform: rotate(180deg) scale(1.2);
          opacity: 0.8;
        }
        100% {
          transform: rotate(360deg) scale(0.5) translateY(-200px);
          opacity: 0;
        }
      }

      @keyframes confettiExplosion {
        0% {
          transform: translateX(-50%) scale(0);
        }
        50% {
          transform: translateX(-50%) scale(1);
        }
        100% {
          transform: translateX(-50%) scale(1);
        }
      }

      @keyframes confettiPiece {
        0% {
          transform: translate(-50%, -50%) rotate(0deg) translateY(0px) translateX(0px);
          opacity: 1;
        }
        100% {
          transform: translate(-50%, -50%) rotate(720deg) translateY(${Math.random() * 400 - 200}px) translateX(${Math.random() * 400 - 200}px);
          opacity: 0;
        }
      }

      /* Enhanced button animation on success */
      .success-button {
        animation: successButtonPulse 0.6s ease-in-out;
      }

      @keyframes successButtonPulse {
        0% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
          box-shadow: 0 8px 25px rgba(1, 181, 116, 0.6) !important;
        }
        100% {
          transform: scale(1);
        }
      }
    `}</style>
    </>
  );
}

export default SendCoins;
